import clsx from "clsx";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Botón del sistema visual de la sección 8.4.
 *
 * POR QUÉ EXISTE
 *   La aceptación de esta tarea exige que **todos** los botones midan 44 px de alto. Con la clase
 *   en un solo sitio, cumplirlo es no poder escribir un botón que no lo cumpla; repartido por las
 *   pantallas, sería cuestión de tiempo que uno se quedara corto.
 *
 * DÓNDE SE APARTA DEL MOCKUP
 *   `docs/mockups/estilo.css` da 34 px a `.btn.small` y a `.chip-toggle`. La sección 8.1 y la
 *   aceptación de esta tarea piden 44 px como mínimo, así que `size="sm"` conserva la letra y el
 *   relleno pequeños pero **no** baja de 44 px de alto. Manda la especificación.
 */

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "md" | "sm";

const VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-pitch text-paper border-pitch font-semibold hover:bg-pitch/90",
  secondary: "bg-surface text-ink border-border hover:border-muted",
  danger: "bg-down/10 text-down border-down/45 hover:bg-down/20",
  ghost: "bg-transparent text-ink border-transparent hover:border-border",
};

const SIZES: Record<ButtonSize, string> = {
  md: "px-4 text-sm",
  sm: "px-3 text-[12.5px]",
};

/** Clases comunes al botón y al enlace con aspecto de botón. */
function buttonClass(
  variant: ButtonVariant,
  size: ButtonSize,
  block: boolean,
  className?: string,
): string {
  return clsx(
    "inline-flex min-h-touch items-center justify-center gap-1.5 rounded-[10px] border",
    "font-sans transition-colors disabled:cursor-not-allowed disabled:opacity-45",
    VARIANTS[variant],
    SIZES[size],
    block && "w-full",
    className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Ocupa todo el ancho disponible. */
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "secondary",
      size = "md",
      block = false,
      className,
      type,
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        // Sin `type` explícito, un botón dentro de un formulario lo envía sin querer.
        type={type ?? "button"}
        className={buttonClass(variant, size, block, className)}
        {...rest}
      />
    );
  },
);

export interface ButtonLinkProps {
  to: string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  className?: string;
}

/** Un enlace de navegación con el aspecto de un botón: sigue siendo un `<a>` para el teclado. */
export function ButtonLink({
  to,
  children,
  variant = "secondary",
  size = "md",
  block = false,
  className,
}: ButtonLinkProps) {
  return (
    <Link
      to={to}
      className={clsx(
        buttonClass(variant, size, block, className),
        "no-underline",
      )}
    >
      {children}
    </Link>
  );
}
